import { account, AUTH_MODE } from "src/handlers/Appwrite";
import { notifyAuthStateChanged } from "src/handlers/Account/useAuthState";
const DEFAULT_AUTH_RETURN_TO = "/components/router";
const AUTH_RETURN_TO_KEY = "auth:returnTo";
const AUTH_AUTO_SIGNIN_SUPPRESS_PREFIX = "auth:autoSigninSuppress:";
const AUTH_AUTO_SIGNIN_SUPPRESS_TTL_MS = 60 * 1000;

// Phase M2: centralized sign-out cache clear.
//
// Preserved keys survive a sign-out:
//   - `version`: the legacy storage-schema marker. Older builds still rely
//     on it; we keep preserving it for forward-compat with any pinned
//     downgrade.
//   - `lastSeenReleaseVersion`: drives the global ReleaseNotesGate. Wiping
//     it on sign-out would re-open the changelog modal every time a user
//     signs back in, which is noisy and not what the user asked for.
const SIGNOUT_PRESERVE_KEYS = new Set(["version", "lastSeenReleaseVersion"]);

// Prefixes matched against every localStorage key on sign-out. Entries
// ending with `:` (Phase I2 auth namespace) are treated as a literal
// prefix; everything else matches either the exact key OR `<prefix>_<id>`
// so we pick up both `hasConnectors` and `hasConnectors_<projectId>`.
const SIGNOUT_LOCALSTORAGE_PREFIXES = [
  // Auth + user cache
  "sessioninfo",
  "userinfo",
  // Per-device editor state (live + rendered)
  "router_data",
  "router_final",
  "switch_data",
  "switch_final",
  "vlan_data",
  // Project gates + per-project connector cache
  "hasConnectors",
  // Auth sub-namespace for returnTo / autoSigninSuppress entries
  "auth:"
];

function shouldClearLocalStorageKey(key) {
  if (!key || SIGNOUT_PRESERVE_KEYS.has(key)) return false;
  return SIGNOUT_LOCALSTORAGE_PREFIXES.some((prefix) => {
    if (prefix.endsWith(":")) {
      return key.startsWith(prefix);
    }
    return key === prefix || key.startsWith(`${prefix}_`);
  });
}

/**
 * Phase M2: broad sign-out cache sweep.
 *
 * Wipes every NetBase-scoped `localStorage` + `sessionStorage` key so a
 * shared device doesn't leak the previous user's project / auth state
 * to the next person. Complements Phase I3's `clearProjectCaches` (which
 * only runs on project switch) by covering auth + cross-project state
 * too.
 *
 * `version` is preserved so the upgrade banner doesn't misfire.
 */
export function clearAllNetbaseData() {
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (shouldClearLocalStorageKey(key)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      // swallow storage errors so one bad key can't block the rest of the
      // sign-out flow.
    }
  });

  try {
    sessionStorage.clear();
  } catch {
    // same as above.
  }
}

// Exported for tests: the exact preserve + prefix lists the sweep uses.
export const __signOutStorage__ = Object.freeze({
  preserve: SIGNOUT_PRESERVE_KEYS,
  prefixes: SIGNOUT_LOCALSTORAGE_PREFIXES,
  shouldClearLocalStorageKey
});

function normalizeReturnTo(returnTo = DEFAULT_AUTH_RETURN_TO) {
  const value = String(returnTo || "").trim();

  if (value.startsWith("/") && !value.startsWith("//")) {
    return value;
  }

  return DEFAULT_AUTH_RETURN_TO;
}

function autoSignInSuppressKey(returnTo = DEFAULT_AUTH_RETURN_TO) {
  return `${AUTH_AUTO_SIGNIN_SUPPRESS_PREFIX}${normalizeReturnTo(returnTo)}`;
}

export function rememberAuthReturnTo(returnTo = DEFAULT_AUTH_RETURN_TO) {
  const normalized = normalizeReturnTo(returnTo);
  sessionStorage.setItem(AUTH_RETURN_TO_KEY, normalized);
  return normalized;
}

export function consumeAuthReturnTo(fallback = DEFAULT_AUTH_RETURN_TO) {
  const stored = sessionStorage.getItem(AUTH_RETURN_TO_KEY);
  sessionStorage.removeItem(AUTH_RETURN_TO_KEY);
  return normalizeReturnTo(stored || fallback);
}

export function suppressAutoSignIn(returnTo = DEFAULT_AUTH_RETURN_TO) {
  sessionStorage.setItem(autoSignInSuppressKey(returnTo), String(Date.now()));
}

export function clearAutoSignInSuppression(returnTo = DEFAULT_AUTH_RETURN_TO) {
  sessionStorage.removeItem(autoSignInSuppressKey(returnTo));
}

export function shouldSuppressAutoSignIn(returnTo = DEFAULT_AUTH_RETURN_TO) {
  const stored = Number(sessionStorage.getItem(autoSignInSuppressKey(returnTo)) || 0);

  if (!stored) {
    return false;
  }

  if (Date.now() - stored > AUTH_AUTO_SIGNIN_SUPPRESS_TTL_MS) {
    clearAutoSignInSuppression(returnTo);
    return false;
  }

  return true;
}

export async function SignIn(returnTo = DEFAULT_AUTH_RETURN_TO) {
  if (AUTH_MODE === "email") {
    rememberAuthReturnTo(returnTo);
    window.dispatchEvent(new CustomEvent("netbase:showEmailLogin", { detail: { returnTo } }));
    return false;
  }

  try {
    const nextReturnTo = rememberAuthReturnTo(returnTo);
    clearAutoSignInSuppression(nextReturnTo);
    const successUrl = new URL("/auth/callback", window.location.origin);
    successUrl.searchParams.set("returnTo", nextReturnTo);
    const failureUrl = new URL("/auth/callback", window.location.origin);
    failureUrl.searchParams.set("error", "oauth");
    failureUrl.searchParams.set("returnTo", nextReturnTo);
    account.createOAuth2Token(
      "oidc",
      successUrl.toString(),
      failureUrl.toString()
    );
  } catch (error) {
    console.error("Sign in failed:", error);
    localStorage.removeItem("sessioninfo");
    localStorage.removeItem("userinfo");
    notifyAuthStateChanged();
    return false;
  }
}

export async function SignInWithEmail(email, password) {
  try {
    await account.createEmailPasswordSession(email, password);
    return await loadCurrentSession();
  } catch (error) {
    console.error("Email sign in failed:", error);
    localStorage.removeItem("sessioninfo");
    localStorage.removeItem("userinfo");
    notifyAuthStateChanged();
    throw error;
  }
}

export async function loadCurrentSession() {
  try {
    const session = await account.getSession("current");
    const user = await account.get();

    localStorage.setItem("sessioninfo", JSON.stringify(session));
    localStorage.setItem("userinfo", JSON.stringify(user));
    notifyAuthStateChanged();

    return { session, user };
  } catch {
    localStorage.removeItem("sessioninfo");
    localStorage.removeItem("userinfo");
    notifyAuthStateChanged();
    return null;
  }
}

export async function SignOut() {
  try {
    await account.deleteSession("current");
  } catch (error) {
    console.error("Sign out failed:", error);
  } finally {
    // Phase M2: single sweep that clears every NetBase-scoped key in one
    // place instead of the previous key-by-key enumeration. See
    // `clearAllNetbaseData` above for the preserve / prefix rules.
    clearAllNetbaseData();
    notifyAuthStateChanged();
  }
}
